import { useState, useCallback, useMemo, useEffect } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/auth';
import { ShiftService } from '@/services/shiftService';
import { 
  Employee, 
  Shift, 
  EmployeeToggleHandler,
  DepartmentToggleHandler,
  ShiftSelectHandler,
  ShiftUpdateHandler
} from '@/types/shifts';
import { 
  groupEmployeesByDepartment, 
  sortDepartments, 
  sortEmployeesInDepartments,
  calculateSelectionStats,
  validateShiftSelection
} from '@/utils/shiftUtils';
import { SHIFT_LABELS } from '@/constants/ui';
;

/**
 * Comprehensive hook for shift management functionality
 */
export function useShiftManagement() {
  // Core state
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [currentAssignments, setCurrentAssignments] = useState<Map<string, { shift_id: string; name: string; code: string | null }>>(new Map());
  const [selectedEmployees, setSelectedEmployees] = useState<Set<string>>(new Set());
  const [selectedDepartments, setSelectedDepartments] = useState<Set<string>>(new Set());
  const [selectedShift, setSelectedShift] = useState<Shift | null>(null);

  // UI state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(true);
  const [isLoadingShifts, setIsLoadingShifts] = useState(true);
  const [isLoadingAssignments, setIsLoadingAssignments] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { user } = useAuth();

  // Memoized derived state
  const departmentGroups = useMemo(() => {
    if (!employees.length) return [];
    
    const grouped = groupEmployeesByDepartment(employees);
    const sorted = sortDepartments(grouped);
    return sortEmployeesInDepartments(sorted);
  }, [employees]);

  const selectedEmployeeIds = useMemo(() => {
    return Array.from(selectedEmployees);
  }, [selectedEmployees]);

  const selectionStats = useMemo(() => {
    return calculateSelectionStats(departmentGroups, selectedEmployees, selectedDepartments);
  }, [departmentGroups, selectedEmployees, selectedDepartments]);

  // Validation
  const validation = useMemo(() => {
    return validateShiftSelection(selectedEmployees, selectedShift);
  }, [selectedEmployees, selectedShift]);

  // Data fetching
  const fetchEmployees = useCallback(async () => {
    if (!user?.companyId) return;

    try {
      setIsLoadingEmployees(true);
      setError(null);
      
      const employeeData = await ShiftService.fetchEmployees(user.companyId);
      // ✅ NEW: Filter out duty roster employees - only show regular shift employees
      const nonDutyRosterEmployees = employeeData.filter(emp => !emp.is_duty_roster);
      setEmployees(nonDutyRosterEmployees);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : SHIFT_LABELS.LOAD_EMPLOYEES_ERROR;
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoadingEmployees(false);
    }
  }, [user?.companyId]);

  const fetchShifts = useCallback(async () => {
    if (!user?.companyId) return;

    try {
      setIsLoadingShifts(true);
      setError(null);
      
      const shiftData = await ShiftService.fetchShifts(user.companyId);
      setShifts(shiftData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : SHIFT_LABELS.LOAD_SHIFTS_ERROR;
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoadingShifts(false);
    }
  }, [user?.companyId]);

  const fetchCurrentAssignments = useCallback(async () => {
    if (!user?.companyId) return;
    try {
      setIsLoadingAssignments(true);
      const map = await ShiftService.fetchCurrentAssignments(user.companyId);
      setCurrentAssignments(map);
    } catch {
      // Error handled silently
    } finally {
      setIsLoadingAssignments(false);
    }
  }, [user?.companyId]);

  // Load data on mount
  useEffect(() => {
    fetchEmployees();
    fetchShifts();
    fetchCurrentAssignments();
  }, [fetchEmployees, fetchShifts, fetchCurrentAssignments]);

  // Employee selection handlers
  const toggleEmployeeSelection: EmployeeToggleHandler = useCallback((employeeId: string) => {
    setSelectedEmployees(prev => {
      const newSet = new Set(prev);
      if (newSet.has(employeeId)) {
        newSet.delete(employeeId);
      } else {
        newSet.add(employeeId);
      }
      return newSet;
    });
  }, []);

  const toggleDepartmentSelection: DepartmentToggleHandler = useCallback((department: string) => {
    const departmentEmployees = departmentGroups.find(g => g.department === department)?.employees || [];
    const total = departmentEmployees.length;
    if (total === 0) return;

    // Determine current state based on selectedEmployees only
    const selectedCount = departmentEmployees.reduce((count, emp) => count + (selectedEmployees.has(emp.id) ? 1 : 0), 0);
    const isCurrentlyChecked = selectedCount === total;

    if (isCurrentlyChecked) {
      // Deselect all in department
      setSelectedEmployees(prevEmployees => {
        const next = new Set(prevEmployees);
        departmentEmployees.forEach(emp => next.delete(emp.id));
        return next;
      });
      setSelectedDepartments(prev => {
        const next = new Set(prev);
        next.delete(department);
        return next;
      });
    } else {
      // Select all in department
      setSelectedEmployees(prevEmployees => {
        const next = new Set(prevEmployees);
        departmentEmployees.forEach(emp => next.add(emp.id));
        return next;
      });
      setSelectedDepartments(prev => {
        const next = new Set(prev);
        next.add(department);
        return next;
      });
    }
  }, [departmentGroups, selectedEmployees]);

  // Department selection state helpers
  const isDepartmentFullySelected = useCallback((department: string) => {
    const departmentEmployees = departmentGroups.find(g => g.department === department)?.employees || [];
    return departmentEmployees.every(emp => selectedEmployees.has(emp.id));
  }, [departmentGroups, selectedEmployees]);

  const isDepartmentPartiallySelected = useCallback((department: string) => {
    const departmentEmployees = departmentGroups.find(g => g.department === department)?.employees || [];
    const selectedCount = departmentEmployees.filter(emp => selectedEmployees.has(emp.id)).length;
    return selectedCount > 0 && selectedCount < departmentEmployees.length;
  }, [departmentGroups, selectedEmployees]);

  // Modal handlers
  const openModal = useCallback(() => {
    if (selectedEmployeeIds.length === 0) {
      toast.error(SHIFT_LABELS.SELECT_EMPLOYEE_ERROR);
      return;
    }
    setIsModalOpen(true);
  }, [selectedEmployeeIds]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedShift(null);
  }, []);

  // Shift selection handler
  const handleShiftSelection: ShiftSelectHandler = useCallback((shift: Shift) => {
    setSelectedShift(shift);
  }, []);

  // Update shifts handler
  const updateShifts: ShiftUpdateHandler = useCallback(async (shiftId: string) => {
    if (!user?.companyId) {
      toast.error(SHIFT_LABELS.COMPANY_ID_ERROR);
      return;
    }

    if (selectedEmployeeIds.length === 0) {
      toast.error(SHIFT_LABELS.NO_EMPLOYEES_SELECTED);
      return;
    }

    // ✅ NEW: Store original assignments for rollback
    const originalAssignments = new Map(currentAssignments);
    
    // ✅ NEW: Get the selected shift details
    const selectedShiftDetails = shifts.find(s => s.id === shiftId);
    if (!selectedShiftDetails) {
      toast.error('Selected shift not found');
      return;
    }

    setIsUpdating(true);
    
    try {
      // ✅ NEW: Update local state immediately (optimistic update)
      const updatedAssignments = new Map(currentAssignments);
      selectedEmployeeIds.forEach(employeeId => {
        updatedAssignments.set(employeeId, {
          shift_id: shiftId,
          name: selectedShiftDetails.name,
          code: selectedShiftDetails.code
        });
      });
      setCurrentAssignments(updatedAssignments);

      const response = await ShiftService.bulkAssignShifts(
        selectedEmployeeIds,
        shiftId,
        user.companyId
      );

      // Process results
      if (response.successCount > 0) {
        toast.success(SHIFT_LABELS.SHIFT_UPDATE_SUCCESS.replace('{count}', response.successCount.toString()));
      }

      if (response.failureCount > 0) {
        // ✅ NEW: Rollback local state on partial failure
        setCurrentAssignments(originalAssignments);
        
        response.results
          .filter(r => !r.success)
          .forEach(result => {
            toast.error(SHIFT_LABELS.SHIFT_UPDATE_ERROR
              .replace('{id}', result.employee_id)
              .replace('{error}', result.error || SHIFT_LABELS.UNKNOWN_ERROR));
          });
      }

      // Clear selection and close modal
      setSelectedEmployees(new Set());
      setSelectedDepartments(new Set());
      closeModal();

    } catch {
      // ✅ NEW: Rollback local state on error
      setCurrentAssignments(originalAssignments);
      toast.error(SHIFT_LABELS.SHIFT_UPDATE_GENERAL_ERROR);
    } finally {
      setIsUpdating(false);
    }
  }, [selectedEmployeeIds, user?.companyId, closeModal, currentAssignments, shifts]);

  // Clear all selections
  const clearSelections = useCallback(() => {
    setSelectedEmployees(new Set());
    setSelectedDepartments(new Set());
  }, []);

  // Refresh data - only use when absolutely necessary
  const refreshData = useCallback(async () => {
    await Promise.all([fetchEmployees(), fetchShifts(), fetchCurrentAssignments()]);
  }, [fetchEmployees, fetchShifts, fetchCurrentAssignments]);

  // Loading state
  const isLoading = isLoadingEmployees || isLoadingShifts || isLoadingAssignments;

  return {
    // Data
    employees,
    shifts,
    departmentGroups,
    currentAssignments,
    selectedEmployees,
    selectedEmployeeIds,
    selectedDepartments,
    selectedShift,
    selectionStats,
    
    // State
    isLoading,
    isLoadingEmployees,
    isLoadingShifts,
    isLoadingAssignments,
    isModalOpen,
    isUpdating,
    error,
    validation,
    
    // Actions
    toggleEmployeeSelection,
    toggleDepartmentSelection,
    isDepartmentFullySelected,
    isDepartmentPartiallySelected,
    openModal,
    closeModal,
    handleShiftSelection,
    updateShifts,
    clearSelections,
    refreshData
  };
} 